import { FormattedAmount } from 'components/FormattedAmount/FormattedAmount';
import { Nothing } from 'components/Nothing/Nothing';
import { Button } from 'components/ui/Button/Button';
import { Heading } from 'components/ui/Heading/Heading';
import { ENTER, SPACE } from 'constants/index';
import React, { useEffect } from 'react';
import { type ConnectedProps, connect } from 'react-redux';
import type { RootState } from 'reducers/types';
import {
  getOrderPrice,
  getOrderPriceWithTaxes,
  getTaxes as getOrderTaxes,
  indianPartnerData,
  orderIncludesTaxes,
} from 'selectors';
import {
  getPaymentAllTaxes,
  getPaymentPrice,
  getPaymentPriceWithoutTaxes,
  hasPayment,
  paymentIncludesTaxes,
} from 'selectors/entities/payment/payment';
import { useTranslations } from 'utils';
import { createMarkup } from 'utils/createMarkup/createMarkup';
import { BackButton } from '../Instructions/BackButton/BackButton';
import './Taxes.scss';

type PropsFromRedux = ConnectedProps<typeof connector>;
type TaxesProps = PropsFromRedux & {
  onNext: () => void;
  onPrev: () => void;
};

const INDIAN_TAX_ID = 'indian_tax';
const TCS_TAX_ID = 'tcs_tax';

function TaxesComponent({
  hasTaxes = false,
  indianPartnerData,
  onNext,
  onPrev,
  price,
  taxes,
  total,
}: TaxesProps) {
  useEffect(() => {
    if (!hasTaxes) {
      onNext();
    }
  }, [hasTaxes, onNext]);

  const i18n = useTranslations();

  if (!hasTaxes) {
    return <Nothing />;
  }

  const hasTCSTax = taxes.some(tax => tax.id === TCS_TAX_ID);

  const taxesContent = taxes.map((tax, index) => {
    const taxId = tax.id === INDIAN_TAX_ID ? TCS_TAX_ID : tax.id;

    return (
      <React.Fragment key={index}>
        <dt>{i18n.t(`checkout.taxes.${taxId}`)}</dt>
        <dd>
          <FormattedAmount value={tax.amount} currency={tax.currency} />
        </dd>
      </React.Fragment>
    );
  });

  return (
    <div className="Taxes flex flex-col h-full" data-testid="taxes-form">
      <div className="flex-1">
        <BackButton onClick={onPrev} />
        <Heading as="h2" size="large" className="fontWeight-semiBold">
          {i18n.t('checkout.taxes.title')}
        </Heading>
        {hasTCSTax ? (
          <div
            className="Taxes-description"
            dangerouslySetInnerHTML={createMarkup(
              i18n.t('taxes.description.gst_and_tcs', indianPartnerData),
            )}
          />
        ) : (
          <div
            className="Taxes-description"
            dangerouslySetInnerHTML={createMarkup(
              i18n.t('taxes.description.gst', indianPartnerData),
            )}
          />
        )}
        <dl>
          <dt>{i18n.t('checkout.taxes.price')}</dt>
          <dd>
            <FormattedAmount {...price} />
          </dd>
          {taxesContent}
        </dl>
        <dl>
          <dt>{i18n.t('checkout.taxes.total')}</dt>
          <dd className="big">
            <FormattedAmount {...total} />
          </dd>
        </dl>
      </div>
      <footer className="pt-6 pb-4">
        <Button
          tone="primary"
          size="full"
          data-testid="confirmButton"
          onMouseDown={onNext}
          onKeyDown={(evt: React.KeyboardEvent<HTMLButtonElement>) =>
            [ENTER, SPACE].includes(evt.keyCode) && onNext()
          }
        >
          {i18n.t('checkout.taxes.accept')}
        </Button>
      </footer>
    </div>
  );
}

const mapStateToProps = (state: RootState) => {
  return hasPayment(state)
    ? {
        hasTaxes: paymentIncludesTaxes(state),
        indianPartnerData: indianPartnerData(state),
        total: getPaymentPrice(state),
        taxes: getPaymentAllTaxes(state),
        price: getPaymentPriceWithoutTaxes(state),
      }
    : {
        hasTaxes: orderIncludesTaxes(state),
        indianPartnerData: indianPartnerData(state),
        total: getOrderPriceWithTaxes(state),
        taxes: getOrderTaxes(state),
        price: getOrderPrice(state),
      };
};

const connector = connect(mapStateToProps);

export const Taxes = connector(TaxesComponent);

// Keep the base component export for testing
export { Taxes as TaxesComponent };
